import React, { useCallback, useState } from 'react';
import { Box, Typography, Button, useTheme, Stack } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useTranslation } from 'react-i18next';
import { formatRelativeDraft } from '../explore/AgentDraftBadge';
import ConfirmationDialog from '../common/ConfirmationDialog';

/**
 * ChatDraftPendingStack — surfaces all pending role drafts above the message
 * stream of the main chat so the user sees Builder-proposed changes without
 * navigating to /explore or /agents/builder.
 *
 * The standalone Builder already has its own DraftBanner/PublishBar — when the
 * user is at /agents/builder/:rolId, the parent (Chat.jsx) filters out that
 * rolId from `drafts` so we don't render two redundant banners.
 *
 * Publish flow mirrors RoleBuilderChat.handlePublishClick: call onGetImpact
 * first, render a ConfirmationDialog with the impact preview, then onPublish.
 * Discard is one-shot.
 */
const ImpactMessage = ({ impactData, t }) => {
  const hasImpact = impactData?.impactedPlans?.length > 0;
  if (!hasImpact) {
    return (
      <span>{t('roleBuilder.draft.publishConfirmBody',
        'Los cambios se aplicarán a todos los chats que usen este agente.')}</span>
    );
  }
  return (
    <Box component="span" sx={{ display: 'block' }}>
      <Typography variant="body2" color="warning.main" sx={{ mb: 1, fontWeight: 600 }}>
        ⚠️ {t('roleBuilder.draft.publishImpactWarning',
          'Estas herramientas se eliminarán del agente:')}
      </Typography>
      <Box component="ul" sx={{ mt: 0, mb: 1, pl: 2 }}>
        {impactData.removedTools.map((tool) => (
          <li key={tool.id}><Typography variant="body2">{tool.name}</Typography></li>
        ))}
      </Box>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {t('roleBuilder.draft.publishImpactPlans',
          'Los siguientes planes quedarán afectados:')}
      </Typography>
      <Box component="ul" sx={{ mt: 0, pl: 2 }}>
        {impactData.impactedPlans.map((plan) => (
          <li key={plan.id}><Typography variant="body2"><strong>{plan.name}</strong></Typography></li>
        ))}
      </Box>
      <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 500 }}>
        {t('roleBuilder.draft.publishImpactContinue',
          'Los pasos del plan que usen estas herramientas dejarán de funcionar correctamente. Esta acción no se puede deshacer automáticamente.')}
      </Typography>
    </Box>
  );
};

const DraftRow = ({ draft, busyState, onPublishClick, onDiscard, onView, t }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const relative = formatRelativeDraft(draft.draftUpdatedAt, t);
  const title = relative
    ? t('chatDraftBanner.title', 'Borrador en "{{name}}" · {{relative}}', { name: draft.name, relative })
    : t('chatDraftBanner.titleNoTime', 'Borrador en "{{name}}"', { name: draft.name });
  const busy = busyState === 'publishing' || busyState === 'discarding';

  return (
    <Box
      data-testid="chat-draft-row"
      data-rol-id={draft.id}
      role="region"
      aria-label={t('chatDraftBanner.ariaLabel', 'Borrador sin publicar')}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        bgcolor: isDark ? 'rgba(255, 167, 38, 0.10)' : 'rgba(255, 167, 38, 0.10)',
        border: `1px solid ${theme.palette.warning.main}55`,
      }}
    >
      {draft.color && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: draft.color,
            flexShrink: 0,
          }}
          aria-hidden="true"
        />
      )}
      <EditNoteIcon sx={{ color: 'warning.main', fontSize: 20, flexShrink: 0 }} />
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          color: 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={title}
      >
        {title}
      </Typography>
      <Button
        size="small"
        variant="text"
        color="inherit"
        onClick={() => onView(draft.id)}
        disabled={busy}
        sx={{ textTransform: 'none', flexShrink: 0, color: 'text.secondary' }}
      >
        {t('chatDraftBanner.actionView', 'Ver')}
      </Button>
      <Button
        size="small"
        variant="text"
        color="inherit"
        onClick={() => onDiscard(draft.id)}
        disabled={busy}
        data-testid="chat-draft-discard"
        sx={{ textTransform: 'none', flexShrink: 0, color: 'text.secondary' }}
      >
        {t('chatDraftBanner.actionDiscard', 'Descartar')}
      </Button>
      <Button
        size="small"
        variant="contained"
        color="primary"
        onClick={() => onPublishClick(draft.id)}
        disabled={busy}
        data-testid="chat-draft-publish"
        sx={{ textTransform: 'none', flexShrink: 0, fontWeight: 600 }}
      >
        {t('chatDraftBanner.actionPublish', 'Publicar')}
      </Button>
    </Box>
  );
};

const ChatDraftPendingStack = ({
  drafts,
  pendingActions = {},
  onPublish,
  onDiscard,
  onView,
  onGetImpact,
}) => {
  const { t } = useTranslation();
  const [confirmRolId, setConfirmRolId] = useState(null);
  const [impactData, setImpactData] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);

  const handlePublishClick = useCallback(async (rolId) => {
    if (preflightLoading) return;
    setPreflightLoading(true);
    setConfirmRolId(rolId);
    try {
      const impact = await onGetImpact(rolId);
      setImpactData(impact || { removedTools: [], impactedPlans: [] });
    } catch {
      setImpactData({ removedTools: [], impactedPlans: [] });
    } finally {
      setPreflightLoading(false);
    }
  }, [onGetImpact, preflightLoading]);

  const handleConfirmPublish = useCallback(async () => {
    const rolId = confirmRolId;
    setConfirmRolId(null);
    setImpactData(null);
    if (rolId) await onPublish(rolId);
  }, [confirmRolId, onPublish]);

  const handleCancelPublish = useCallback(() => {
    setConfirmRolId(null);
    setImpactData(null);
  }, []);

  if (!drafts || drafts.length === 0) return null;

  return (
    <Box data-testid="chat-draft-pending-stack" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
      <Stack spacing={1}>
        {drafts.map((draft) => (
          <DraftRow
            key={draft.id}
            draft={draft}
            busyState={pendingActions[draft.id]}
            onPublishClick={handlePublishClick}
            onDiscard={onDiscard}
            onView={onView}
            t={t}
          />
        ))}
      </Stack>
      <ConfirmationDialog
        open={Boolean(confirmRolId) && !preflightLoading}
        onClose={handleCancelPublish}
        onConfirm={handleConfirmPublish}
        title={t('roleBuilder.draft.publishConfirmTitle', 'Publicar agente')}
        message={<ImpactMessage impactData={impactData} t={t} />}
        confirmText={t('roleBuilder.draft.publishConfirmAction', 'Publicar')}
        confirmColor={impactData?.impactedPlans?.length > 0 ? 'error' : 'primary'}
      />
    </Box>
  );
};

export default ChatDraftPendingStack;
